import { useReducer, useCallback, useRef } from "react";
import { AppendixFile, Pentest, Template, Vulnerability } from "../sdkStructs";

// Define the state for our PDF generation
type PdfState = {
  blob: Blob | undefined;
  loading: boolean;
  error: string | null;
  isGenerating: boolean;
  outputFormat: "pdf" | "docx";
};

// Define actions for our PDF state reducer
type PdfAction =
  | { type: "START_GENERATION" }
  | { type: "GENERATION_SUCCESS"; payload: Blob }
  | { type: "GENERATION_FAILURE"; error: string }
  | { type: "GENERATION_ABORTED" }
  | { type: "RESET" }
  | { type: "SET_OUTPUT_FORMAT"; format: "pdf" | "docx" };

interface PdfGenerationResult extends PdfState {
  generatePdf: (
    template: Template | null,
    pentest: Pentest | undefined,
    pentestVulns: Vulnerability[],
    appendix: AppendixFile[]
  ) => Promise<void>;
  resetPdf: () => void;
  abortGeneration: () => void;
}

// PDF state reducer
function pdfReducer(state: PdfState, action: PdfAction): PdfState {
  switch (action.type) {
    case "START_GENERATION":
      return { ...state, loading: true, isGenerating: true, error: null };
    case "GENERATION_SUCCESS":
      return {
        ...state,
        blob: action.payload,
        loading: false,
        error: null,
        isGenerating: false,
      };
    case "GENERATION_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.error,
        isGenerating: false,
      };
    case "GENERATION_ABORTED":
      return {
        ...state,
        loading: false,
        isGenerating: false,
      };
    case "RESET":
      return {
        ...state,
        blob: undefined,
        loading: false,
        error: null,
        isGenerating: false,
      };
    case "SET_OUTPUT_FORMAT":
      return {
        ...state,
        outputFormat: action.format
      };
    default:
      return state;
  }
}

// Custom hook for PDF generation
export default function usePdfGeneration(token: string): PdfGenerationResult {
  const [state, dispatch] = useReducer(pdfReducer, {
    blob: undefined,
    loading: false,
    error: null,
    isGenerating: false,
    outputFormat: "pdf"
  });

  // Keep a reference to the current abort controller
  const abortControllerRef = useRef<AbortController | null>(null);

  // Function to abort the current PDF generation
  const abortGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('Aborting PDF generation');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      dispatch({ type: "GENERATION_ABORTED" });
    }
  }, []);

  const generatePdf = useCallback(
    async (template: Template | null, pentest: Pentest | undefined, pentestVulns: Vulnerability[], appendix: AppendixFile[]) => {
      if (state.isGenerating) {
        abortGeneration();
      }

      if (!template) {
        dispatch({
          type: "GENERATION_FAILURE",
          error: "No template provided for PDF generation"
        });
        return;
      }

      // Set output format based on template FileType
      if (template.FileType) {
        dispatch({ type: "SET_OUTPUT_FORMAT", format: template.FileType as "pdf" | "docx" });
      }

      dispatch({ type: "START_GENERATION" });

      try {
        // Create a new abort controller for this request
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        const pdfServiceUrl = import.meta.env.PROD ? import.meta.env.VITE_PDF_URL : 'http://localhost:3000';
        const endpoint = `${pdfServiceUrl}/generate`;

        // Prepare request payload
        const requestData: any = {
          template: {
            ...template,
            FileType: template.FileType || "pdf", // Ensure FileType is set
          },
          pentest,
          vulnerabilities: pentestVulns || [],
          appendix: appendix || [],
        };

        // If template has a DocxPath, set the File property for the microservice
        if (template.DocxPath) {
          requestData.template.File = template.DocxPath;
        }

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
          signal,
        });

        // Check if the request was aborted during the fetch
        if (signal.aborted) {
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.message || `Document generation failed with status: ${response.status}`
          );
        }

        const documentBlob = await response.blob();
        dispatch({ type: "GENERATION_SUCCESS", payload: documentBlob });
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Document generation was aborted by user');
          return;
        }

        const errorMessage = error instanceof Error
          ? error.message
          : "Unknown error during document generation";

        dispatch({
          type: "GENERATION_FAILURE",
          error: errorMessage,
        });
      } finally {
        abortControllerRef.current = null;
      }
    },
    [token, state.isGenerating, abortGeneration],
  );

  const resetPdf = useCallback(() => {
    abortGeneration();
    dispatch({ type: "RESET" });
  }, [abortGeneration]);

  return {
    ...state,
    generatePdf,
    resetPdf,
    abortGeneration,
  };
}
